import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "rpb_main-content--no-sidepadding hotels-nearby" }

import OfferGrid from '@lmt-rpb/OfferGrid/OfferGrid.vue';
import type { AxiosError } from 'axios';
import axios from 'axios';
import request from '@components/common/services/request';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import { isClientGermany } from '@utils/environmentUtils';

import { getSearchParams } from '@modules/updateUrls';
import type { SearchFormDataType } from '@interfaces/search-form';
import { computed, ref, watch } from 'vue';
import type { HeaderProps } from '@lmt-rpb/ComponentHeader/componentHeaderTypes';
import type { HotelBoxData } from '@/interfaces/components/hotelBoxData';
import type { GeoAreaFilter, TravelType } from '@/interfaces/common';
import {
	type BestPackageOfferForHotelList,
	type BestPackageOfferForHotelListResponse,
} from '@/interfaces/api/v1-best-package-offer-for-hotel-list';
import {
	type BestAccommodationOfferForHotelList,
	type BestAccommodationOfferForHotelListResponse,
} from '@/interfaces/api/v1-best-accommodation-offer-for-hotel-list';
import { useStore } from '@/components/common/store';
import NearbyHotelsAnalytics from '@/js/modules/analytics/NearbyHotelsAnalytics';
import { transformList } from './helper';
import type { ClientCurrency } from '@global-js/constants';
import { getDefaultCurrency } from '@utils/environmentUtils';

interface Props {
	cityName: string;
	hotelName: string;
	cyid: number;
	latitude: number;
	longitude: number;
	searchDistance: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'HotelsNearby',
  props: {
    cityName: {},
    hotelName: {},
    cyid: {},
    latitude: {},
    longitude: {},
    searchDistance: {}
  },
  emits: ['HotelsNearby:HotelsFound'],
  setup(__props: any, { emit: __emit }) {

const store = useStore();
const props = __props;

const emit = __emit;

const headerProps = ref<HeaderProps>({
	title: 'Hotels in der Nähe',
	subtitle: '',
	mobileTitle: 'Hotels in der Nähe',
	mobileSubtitle: '',
	moreText: 'Mehr',
	link: '',
});

const items = ref<HotelBoxData[] | never[]>([]);

const offersFetched = ref<BestAccommodationOfferForHotelList[] | BestPackageOfferForHotelList[]>([]);

const error = ref(false);

const loading = ref(true);

const destinationName = props.cityName;

const moreLink = ref(headerProps.value.link);

const currencySymbol = ref<ClientCurrency>(getDefaultCurrency());

const showSuns = isClientGermany();

const formData = computed((): SearchFormDataType => store.state.searchMask);

const travelType = computed(() => store.getters['searchMask/travelType']);
const travelTypeApiResponse = computed<TravelType>(() => store.getters['searchMask/travelTypeApiResponse']);

const fetchUrl = computed((): string => {
	const {
		hotelList: { packages, hotels },
	} = store.state.config;
	return formData.value.onlyHotel ? hotels : packages;
});

function onHotelsNearbyLoaded() {
	NearbyHotelsAnalytics.fromNearbyOffersLoaded(items.value, travelTypeApiResponse.value).fireViewItemList();
}

function onHotelBoxClickAnalytics(event: { hotelData: HotelBoxData; index: number }) {
	NearbyHotelsAnalytics.fromHotelOfferClicked(event.hotelData, travelTypeApiResponse.value, event.index)?.fireSelectItem();
}

const calculateGeolocation = (longitude: number, latitude: number, searchDistance: number): GeoAreaFilter => {
	const earth = 6378.137; // radius of the earth in kilometer
	const pi = Math.PI;
	const distance = searchDistance * 1000; // distance of centerpoint in meter
	const meter = 1 / (((2 * pi) / 360) * earth) / 1000; // 1 meter in degree
	const cos = Math.cos;

	const northLatitude = latitude + distance * meter;
	const southLatitude = latitude - distance * meter;
	const eastLongitude = longitude + (distance * meter) / cos(latitude * (pi / 180));
	const westLongitude = longitude - (distance * meter) / cos(latitude * (pi / 180));

	return {
		lowerLeft: {
			latitude: southLatitude,
			longitude: westLongitude,
		},
		upperRight: {
			latitude: northLatitude,
			longitude: eastLongitude,
		},
	};
};

const geoArea = ref(calculateGeolocation(props.longitude, props.latitude, props.searchDistance));

const apiData = store.getters['searchMask/apiData'];

const fetchOffers = (): void => {
	loading.value = true;
	error.value = false;
	const exclude = travelType.value === 'hotel' ? ['DepartureAirport', 'DirectFlight', 'Transfer', 'DepartureTime', 'ArrivalTime'] : [];
	exclude.push('DefiniteStartDate', 'HotelIffCode', 'Regions', 'Cities');

	const bodyParams: Partial<SearchFormDataType> = {
		numberOfResults: 5,
		sortingPricePopular: 'Popular',
		geoAreaFilter: geoArea.value,
	};

	const body = apiData(bodyParams, exclude);
	const uri = fetchUrl.value;

	request<BestAccommodationOfferForHotelListResponse | BestPackageOfferForHotelListResponse>({ url: uri, data: body }, 'hotelsNearby')
		.then((resp) => {
			if (!resp?.Offers?.length) {
				return;
			}

			offersFetched.value = resp.Offers;
			const transformedList: HotelBoxData[] = transformList(resp.Offers);
			items.value = transformedList.filter((hotel) => hotel.HotelIffCode !== formData.value.hotelId).slice(0, 4);
		})
		.catch((axiosError: AxiosError) => {
			if (!axios.isCancel(axiosError)) {
				console.error('HotelsNearby: ', axiosError);
			}

			error.value = true;
		})
		.finally(() => {
			emit('HotelsNearby:HotelsFound', items.value.length > 0);
			loading.value = false;
		});
};

watch(
	() => formData.value,
	() => {
		fetchOffers();
		const extendUrl = getSearchParams();
		moreLink.value = `/hotels/o/${props.cyid}/${extendUrl === '' ? '' : `?${extendUrl}`}`;
		headerProps.value.link = moreLink.value;
	},
	{ deep: true, immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loading.value)
      ? (_openBlock(), _createBlock(Loading, {
          key: 0,
          class: "hotels-nearby__loader"
        }))
      : _createCommentVNode("v-if", true),
    (!loading.value && items.value.length)
      ? (_openBlock(), _createBlock(OfferGrid, {
          key: 1,
          "hotel-list": items.value,
          "currency-symbol": currencySymbol.value,
          "show-suns": _unref(showSuns),
          "destination-name": _unref(destinationName),
          slider: true,
          "four-columns": true,
          headline: "Hotels in der Nähe",
          "headline-mobile": "Hotels in der Nähe",
          "bottom-c-t-a-text": 'Alle Hotels in ' + _unref(destinationName),
          "more-link": moreLink.value,
          "has-promotion-analytics": false,
          "header-props": headerProps.value,
          onVnodeMounted: onHotelsNearbyLoaded,
          onHotelBoxClick: _cache[0] || (_cache[0] = ($event: any) => (onHotelBoxClickAnalytics($event)))
        }, null, 8 /* PROPS */, ["hotel-list", "currency-symbol", "show-suns", "destination-name", "bottom-c-t-a-text", "more-link", "header-props"]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})